import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Activity, CalendarClock } from 'lucide-react';
import { BowelPatternStrip } from './BowelPatternStrip';
import { BowelObservationTags } from './BowelObservationTags';

/**
 * Day 4 — Clinical Trend Summary card (read-only, observational).
 *
 * Shows a compact 7-day view: pattern strip + headline counters + neutral tags.
 * Fails safe: renders nothing when `summary` is missing.
 * No diagnostic language, no AI-generated insights.
 */

const BRISTOL_LABEL = {
  bristol_1: 'Type 1 (hard lumps)',
  bristol_2: 'Type 2 (lumpy)',
  bristol_3: 'Type 3 (cracked)',
  bristol_4: 'Type 4 (smooth & soft)',
  bristol_5: 'Type 5 (soft blobs)',
  bristol_6: 'Type 6 (mushy)',
  bristol_7: 'Type 7 (liquid)',
};

function Stat({ label, value, testId }) {
  return (
    <div className="min-w-[72px]" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold leading-tight">{value}</div>
    </div>
  );
}

function formatLastEntry(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

export function BowelTrendSummaryCard({ summary }) {
  if (!summary || typeof summary !== 'object') return null;
  const window = summary.window_days || 7;
  const latestStool = summary.latest_stool_type ? (BRISTOL_LABEL[summary.latest_stool_type] || summary.latest_stool_type) : '—';
  return (
    <Card className="border-slate-200" data-testid="bowel-trend-summary-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-rose-600" />
            <span className="text-sm font-semibold">Last {window} days — observational summary</span>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="bowel-trend-last-entry">
            <CalendarClock className="w-3 h-3" />
            Last entry: <span className="font-medium text-foreground">{formatLastEntry(summary.last_entry_at)}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <Stat testId="trend-total-entries" label="Entries" value={summary.total_entries_7d ?? 0} />
          <Stat testId="trend-days-action" label="Days w/ action" value={summary.days_with_bowel_action ?? 0} />
          <Stat testId="trend-days-no-action" label="Days w/ no action" value={summary.days_with_no_bowel_action_recorded ?? 0} />
          <Stat testId="trend-days-empty" label="Days w/o entries" value={summary.days_without_entries ?? 0} />
          <Stat testId="trend-latest-stool" label="Latest stool" value={latestStool} />
        </div>

        <div className="pt-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">7-day pattern</div>
          <BowelPatternStrip strip={summary.pattern_strip} windowDays={window} />
        </div>

        {Array.isArray(summary.observation_tags) && summary.observation_tags.length > 0 && (
          <div className="pt-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Observations</div>
            <BowelObservationTags tags={summary.observation_tags} />
          </div>
        )}

        <p className="text-[10px] text-muted-foreground pt-1">
          Observational summary based on recorded entries. Not a clinical assessment.
        </p>
      </CardContent>
    </Card>
  );
}

export default BowelTrendSummaryCard;
